import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getLiveAttendanceStatus } from "../../api/attendance";
import { formatAttendanceTime } from "../../utils/attendanceTimeFormat";
import {
  ReportBarChart,
  ReportPieChart,
} from "../reports/ReportCharts";
import EmployeeHistoryView from "./EmployeeHistoryView";
import "../reports/ReportsHub.css";
import "./LiveAttendanceBoard.css";
import "./EmployeeHistoryView.css";

const STATUS_META = {
  working: {
    key: "working",
    title: "شغال الآن",
    hint: "مسجّل حضور ومفتوح",
    cls: "is-working",
  },
  late: {
    key: "late",
    title: "متأخر",
    hint: "عدّى معاد الشيفت من غير حضور",
    cls: "is-late",
  },
  notArrived: {
    key: "notArrived",
    title: "لسه مجاش",
    hint: "متوقع حضوره ولسه ماسجّلش",
    cls: "is-waiting",
  },
};

const STATUS_LABELS = {
  working: "شغال الآن",
  late: "متأخر",
  notArrived: "لسه مجاش",
};

function LiveEmployeeCard({ row, onOpen }) {
  return (
    <button
      type="button"
      className={`live-att-card ${STATUS_META[row.status]?.cls || ""}`}
      onClick={() => onOpen(row)}
    >
      <span className="live-att-card-dot" aria-hidden="true" />
      <span className="live-att-card-body">
        <span className="live-att-card-name">{row.fullName}</span>
        <span className="live-att-card-meta">
          {row.scheduledStart && row.scheduledEnd
            ? `${row.scheduledStart} — ${row.scheduledEnd}`
            : "بدون موعد"}
          {row.jobTitle ? ` · ${row.jobTitle}` : ""}
        </span>
      </span>
      {row.status === "working" ? (
        <span className="live-att-card-badge">نشط</span>
      ) : null}
      {row.status === "late" && row.lateMinutes > 0 ? (
        <span className="live-att-card-badge is-warn">
          +{row.lateMinutes} د
        </span>
      ) : null}
    </button>
  );
}

function DetailModal({ row, onClose, onOpenHistory }) {
  if (!row) return null;
  return (
    <div className="live-att-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="live-att-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="live-att-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="live-att-modal-head">
          <div>
            <h3 id="live-att-modal-title">{row.fullName}</h3>
            <p>
              {STATUS_LABELS[row.status] || row.status}
              {row.employeeCode ? ` · ${row.employeeCode}` : ""}
            </p>
          </div>
          <button type="button" className="live-att-modal-close" onClick={onClose}>
            إغلاق
          </button>
        </div>
        <dl className="live-att-modal-grid">
          <div>
            <dt>الشيفت المتوقع</dt>
            <dd>
              {row.scheduledStart && row.scheduledEnd
                ? `${row.scheduledStart} — ${row.scheduledEnd}`
                : "—"}
            </dd>
          </div>
          <div>
            <dt>مصدر الموعد</dt>
            <dd>{row.hasTimesheet ? "تايم شيت" : "موعد افتراضي"}</dd>
          </div>
          <div>
            <dt>الحضور</dt>
            <dd>
              {row.checkInAt ? formatAttendanceTime(row.checkInAt) : "لم يسجّل"}
            </dd>
          </div>
          <div>
            <dt>الانصراف</dt>
            <dd>
              {row.checkOutAt ? formatAttendanceTime(row.checkOutAt) : "—"}
            </dd>
          </div>
          <div>
            <dt>دقائق التأخير</dt>
            <dd>{row.lateMinutes ?? 0}</dd>
          </div>
          <div>
            <dt>المصدر</dt>
            <dd>{row.source || "—"}</dd>
          </div>
        </dl>
        <div className="live-att-modal-actions">
          <button
            type="button"
            className="live-att-btn primary"
            onClick={() => onOpenHistory(row)}
          >
            عرض السجل الكامل
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LiveAttendanceBoard({
  getEmployeeById,
  title = "الحضور الآن في الفرع",
}) {
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [activeList, setActiveList] = useState("working");

  const load = useCallback(async (nextBranchId) => {
    setLoading(true);
    try {
      const result = await getLiveAttendanceStatus(
        nextBranchId ? { branchId: nextBranchId } : {},
      );
      setData(result);
      setError(null);
      if (result?.branchId && !nextBranchId) {
        setBranchId(result.branchId);
      }
    } catch (err) {
      setError(err?.message || "تعذر تحميل الحضور اللحظي");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(branchId || undefined);
    const timer = setInterval(() => {
      load(branchId || undefined);
    }, 60_000);
    return () => clearInterval(timer);
  }, [branchId, load]);

  const pieData = useMemo(
    () =>
      (data?.distribution || []).map((row) => ({
        name: row.name,
        value: row.value,
      })),
    [data],
  );

  const trendData = useMemo(
    () =>
      (data?.trends || []).map((row) => ({
        name: row.date?.slice(5) || row.date,
        present: row.present,
        late: row.late,
        absent: row.absent,
      })),
    [data],
  );

  const listRows = data?.[activeList] || [];

  const openHistory = (row) => {
    const fromContext = getEmployeeById?.(row.employeeId);
    setSelected(null);
    setHistoryEmployee(
      fromContext || {
        id: row.employeeId,
        name: row.fullName,
        fullName: row.fullName,
        code: row.employeeCode,
        employeeCode: row.employeeCode,
        jobTitle: row.jobTitle,
        branchId: row.branchId,
        userId: row.userId,
      },
    );
  };

  if (historyEmployee) {
    return (
      <EmployeeHistoryView
        employee={historyEmployee}
        onBack={() => setHistoryEmployee(null)}
      />
    );
  }

  return (
    <section className="live-att-board" dir="rtl">
      <div className="live-att-head">
        <div>
          <h2 className="live-att-title">{title}</h2>
          <p className="live-att-sub">
            مين شغال · مين متأخر · مين لسه مجاش
            {data?.date ? ` · ${data.date}` : ""}
          </p>
        </div>
        <div className="live-att-controls">
          <label className="live-att-filter">
            <span>المكان</span>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {(data?.branches || []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="live-att-btn"
            onClick={() => load(branchId || undefined)}
            disabled={loading}
          >
            تحديث
          </button>
        </div>
      </div>

      {error ? <p className="live-att-error">{error}</p> : null}

      <div className="live-att-summary">
        {Object.values(STATUS_META).map((meta) => (
          <button
            key={meta.key}
            type="button"
            className={`live-att-summary-card ${meta.cls}${
              activeList === meta.key ? " is-active" : ""
            }`}
            onClick={() => setActiveList(meta.key)}
          >
            <span className="live-att-summary-dot" aria-hidden="true" />
            <span className="live-att-summary-label">{meta.title}</span>
            <strong className="live-att-summary-count">
              {data?.counts?.[meta.key] ?? (loading ? "…" : 0)}
            </strong>
            <span className="live-att-summary-hint">{meta.hint}</span>
          </button>
        ))}
      </div>

      <div className="live-att-charts">
        <div className="live-att-chart-card">
          <h3>توزيع اليوم</h3>
          <div className="reports-chart-body">
            <ReportPieChart data={pieData} height={220} />
          </div>
        </div>
        <div className="live-att-chart-card">
          <h3>توجه 7 أيام</h3>
          <div className="reports-chart-body">
            <ReportBarChart
              data={trendData}
              height={220}
              bars={[
                { dataKey: "present", name: "حضور", color: "#27ae60" },
                { dataKey: "late", name: "تأخير", color: "#e67e22" },
                { dataKey: "absent", name: "غياب", color: "#c0392b" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="live-att-list-wrap">
        <div className="live-att-list-head">
          <h3>{STATUS_META[activeList]?.title || "الموظفون"}</h3>
          <span>{listRows.length} موظف</span>
        </div>
        {loading && !data ? (
          <p className="live-att-empty">جاري التحميل…</p>
        ) : listRows.length === 0 ? (
          <p className="live-att-empty">لا يوجد موظفين في هذه الحالة</p>
        ) : (
          <div className="live-att-grid">
            {listRows.map((row) => (
              <LiveEmployeeCard
                key={row.employeeId}
                row={row}
                onOpen={setSelected}
              />
            ))}
          </div>
        )}
      </div>

      <DetailModal
        row={selected}
        onClose={() => setSelected(null)}
        onOpenHistory={openHistory}
      />
    </section>
  );
}
